import { routes } from '@fhs-legacy/frontend/src/utils/routing';

// This is the subset of the CMS navigation-page shape needed by More. Both legacy and localized
// fields are supported because the navigation response differs by market and CMS configuration.
export type NavigationPage = {
  __typename?: string | null;
  title?: string | null;
  localeTitle?: { locale?: string | null } | null;
  path?: { current?: string | null } | null;
  localePath?: { locale?: { current?: string | null } | null } | null;
};

export type NavigationEntry = NavigationPage & {
  name?: { locale?: string | null } | null;
  pages?: readonly NavigationEntry[] | null;
};

export type MoreLink = { label: string; path: string; onPress?: () => void };

/**
 * CMS slugs may omit their leading slash. Normalize internal paths for Expo Router while leaving
 * absolute URLs and protocol links untouched.
 *
 * @example
 * addPathSlash('privacy-policy') // '/privacy-policy'
 * addPathSlash('https://example.com') // 'https://example.com'
 */
const addPathSlash = (path: string) =>
  path.startsWith('/') || /^(https?:|mailto:|tel:)/i.test(path) ? path : `/${path}`;

/**
 * Converts the CMS navigation tree into the flat link collection needed by the More screen.
 * NavigationSection entries are containers rather than destinations, so their children are
 * recursively unwrapped. Entries without both a display label and destination are ignored.
 *
 * @example
 * flattenNavigationEntries([
 *   {
 *     __typename: 'NavigationSection',
 *     pages: [{ title: 'Privacy Policy', path: { current: 'privacy-policy' } }],
 *   },
 * ]);
 * // [{ label: 'Privacy Policy', path: '/privacy-policy' }]
 */
export function flattenNavigationEntries(entries: readonly NavigationEntry[]): MoreLink[] {
  return entries.flatMap(entry => {
    if (entry.__typename === 'NavigationSection' || entry.pages) {
      return flattenNavigationEntries(entry.pages ?? []);
    }

    const label = entry.localeTitle?.locale || entry.title || '';
    const path = entry.localePath?.locale?.current || entry.path?.current || '';
    return label && path ? [{ label, path: addPathSlash(path) }] : [];
  });
}

/**
 * Content editors may vary capitalization, punctuation, or use "&" instead of "and". Removing
 * those differences lets us identify a CMS page without coupling navigation to its exact title.
 *
 * @example
 * normalize('Terms & Policies') // 'termsandpolicies'
 * normalize('TERMS and POLICIES!') // 'termsandpolicies'
 */
const normalize = (value: string) =>
  value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');

/**
 * Finds the current CMS destination for a required More row while keeping the product-approved
 * row label stable. Aliases cover known historical CMS titles, and fallbackPath keeps the link
 * functional when navigation content is unavailable or a page has been renamed unexpectedly.
 *
 * @example
 * resolveCmsLink(
 *   [{ label: 'Frequently Asked Questions', path: '/help/faq' }],
 *   'FAQs',
 *   '/faq',
 *   ['Frequently Asked Questions']
 * );
 * // { label: 'FAQs', path: '/help/faq' }
 *
 * resolveCmsLink([], 'FAQs', '/faq');
 * // { label: 'FAQs', path: '/faq' }
 */
export function resolveCmsLink(
  links: readonly MoreLink[],
  label: string,
  fallbackPath: string,
  aliases: readonly string[] = []
): MoreLink {
  const names = [label, ...aliases].map(normalize);
  const match = links.find(link => names.includes(normalize(link.label)));
  return { label, path: match?.path ?? fallbackPath };
}

// Product routes use the shared route constants so they remain aligned with the existing account,
// loyalty, and support flows.
export const accountLinks = (cateringHref: string, showGiftCards: boolean): MoreLink[] => [
  { label: 'Account Info', path: routes.info },
  { label: 'Recent Orders', path: routes.orders },
  { label: 'Payment Methods', path: routes.payment },
  ...(showGiftCards ? [{ label: 'Gift Cards', path: routes.giftCards }] : []),
  { label: 'Order Catering', path: cateringHref },
];

export const loyaltyLinks: MoreLink[] = [
  { label: 'Rewards', path: `${routes.offersV2}?section=rewards` },
  { label: 'Offers', path: routes.offersV2 },
];

// Static-content destinations are resolved through the CMS so localized or editor-managed slugs
// continue to work. App-owned destinations still use the shared route constants directly.
export const supportLinks = (cmsLinks: readonly MoreLink[]): MoreLink[] => [
  { label: 'Contact Support', path: routes.support },
  resolveCmsLink(cmsLinks, 'FAQs', '/faq', ['FAQ', 'Frequently Asked Questions']),
  { label: 'Claim Points', path: '/claim-points-page' },
];

export const aboutLinks = (
  cmsLinks: readonly MoreLink[],
  {
    foundationHref,
    franchiseHref,
    careersHref,
  }: { foundationHref: string; franchiseHref: string; careersHref: string }
): MoreLink[] => [
  resolveCmsLink(cmsLinks, 'About Firehouse Subs', '/about-firehouse-subs', ['About Us']),
  resolveCmsLink(cmsLinks, 'Terms of Service', '/terms-of-service'),
  resolveCmsLink(cmsLinks, 'Privacy Policy', '/privacy-policy'),
  { label: 'Locations', path: routes.storeLocator },
  resolveCmsLink(cmsLinks, 'Nutritional Information', '/nutrition'),
  { label: 'Public Safety Foundation', path: foundationHref },
  { label: 'Own a Franchise', path: franchiseHref },
  { label: 'Explore Careers', path: careersHref },
];

export const policyLinks = (cmsLinks: readonly MoreLink[]): MoreLink[] => [
  resolveCmsLink(cmsLinks, 'Offer Terms', '/offer-terms'),
  resolveCmsLink(cmsLinks, 'Delivery Terms', '/delivery-terms'),
  resolveCmsLink(cmsLinks, 'Mobile Ordering and Payment', '/mobile-ordering-and-payment-terms', [
    'Mobile Ordering and Payment Terms',
  ]),
  resolveCmsLink(cmsLinks, 'Rewards Terms', '/rewards-terms', ['Firehouse Rewards Terms']),
  resolveCmsLink(cmsLinks, 'Request My Information', routes.accountRequestInfo),
  resolveCmsLink(cmsLinks, 'Accessibility Statement', '/accessibility'),
  resolveCmsLink(cmsLinks, 'Unsolicited Idea Policy', '/unsolicited-idea-policy'),
  resolveCmsLink(cmsLinks, 'Do Not Sell or Share My Personal Information', '/ccpa-compliance', [
    'Do Not Sell My Personal Information',
  ]),
  resolveCmsLink(cmsLinks, '#FirehouseSubsYes', '/firehousesubsyes'),
];
